/* eslint-disable react/prop-types */
import React, { useLayoutEffect, useRef } from 'react';
import { Tooltip } from 'antd';
import { FaBus, FaRegHandshake } from 'react-icons/fa';

import {
  BookOutlined,
  CalendarOutlined,
  CopyOutlined,
  DeleteOutlined,
  InfoOutlined,
  ShareAltOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';

import Utils from '../../Assets/Scripts/Utils';

const OFFICIAL_TRANSPORT_INCLUDED_TOOLTIP = 'Transporte Oficial: Incluso';
const QUOTE_RESPONSIBLE_ICON_COLOR = '#fabc2c';
const MISSING_QUOTE_RESPONSIBLE_TOOLTIP = 'Sem responsável pelo negócio';

export const stripNativeTitleFromSchedulerAppointment = (element) => {
  const schedulerAppointment = element?.closest?.('.dx-scheduler-appointment');

  if (!schedulerAppointment || typeof schedulerAppointment.removeAttribute !== 'function') {
    return;
  }

  schedulerAppointment.removeAttribute('title');
};

const renderAgendaTransportTooltipTitle = (transportIcon) => {
  if (transportIcon.tooltipKind !== 'vehicles') {
    return OFFICIAL_TRANSPORT_INCLUDED_TOOLTIP;
  }

  return (
    <div>
      {transportIcon.vehicleIdentifications.map((identification) => (
        <div key={identification}>{identification}</div>
      ))}
    </div>
  );
};

export default function Appointment({
  itemData: {
    data: { appointmentData },
  },
  className,
  schedulerRef,
  screen,
  setTaskRescheduling,
  removeAppointmentByIndex,
}) {
  const appointmentRootRef = useRef(null);
  const isTooltip = screen === 'appointmentTooltip';

  useLayoutEffect(() => {
    stripNativeTitleFromSchedulerAppointment(appointmentRootRef.current);
  });

  if (appointmentData.allDay) {
    return (
      <div key="cell" ref={appointmentRootRef} className="booked-day-cell">
        Dia Reservado
      </div>
    );
  }

  const type = appointmentData.scheduleType;

  let stageObject;

  if (type === 'driver') {
    const currentUser = JSON.parse(localStorage.getItem('conecta__userData'));
    const driver =
      currentUser && appointmentData.driverList?.find((d) => d.userId === currentUser.id);

    stageObject = driver?.stage || appointmentData.driver?.stage || appointmentData.stage;
  } else if (type) {
    stageObject = appointmentData[type]?.stage || appointmentData.stage;
  } else {
    stageObject = appointmentData.stage;
  }

  const stage = stageObject?.id || 999;
  const backgroundColor = stageObject?.color;

  // Define os IDs dos estágios que devem ter texto branco
  const stagesWithWhiteText = [
    12, // Reservado
    14, // Aguardando Documentação
    15, // Faturado/Aguardando Documentação
    16, // Aguardando Faturamento
  ];

  const currentStageId = stageObject?.id;

  // Define a cor do texto com base no ID do estágio
  const textColor = stagesWithWhiteText.includes(currentStageId) ? '#fff' : '#121212';

  const mainInstructorStatusColor = () => {
    if (appointmentData?.mainInstructor) {
      return appointmentData?.mainInstructor.stage.color;
    }
    return '#b1b1b1';
  };

  const instructorTagTitle = () => {
    switch (appointmentData?.mainInstructor?.stage?.id) {
      case 1:
        return `${appointmentData?.mainInstructor.userName?.toUpperCase()}: Aguardando aceite`;
      case 3:
        return `${appointmentData?.mainInstructor.userName?.toUpperCase()}: Aceito`;
      case 5:
        return `${appointmentData?.mainInstructor.userName?.toUpperCase()}: Concluído`;
      default:
        return 'Sem Instrutor vinculado';
    }
  };

  const quoteResponsibleTagTitle = () => {
    const responsibleName = appointmentData?.quoteResponsible;

    if (typeof responsibleName === 'string' && responsibleName.trim() !== '') {
      return responsibleName.trim().toUpperCase();
    }

    return MISSING_QUOTE_RESPONSIBLE_TOOLTIP;
  };

  const handleDelete = () => {
    schedulerRef?.current?.instance?.deleteAppointment(appointmentData);
  };

  const hasProductName = () =>
    typeof appointmentData.text === 'string' && appointmentData.text.trim() !== '';

  const renderTrainingTag = () => {
    if (!hasProductName()) {
      return null;
    }

    return (
      <Tooltip placement="bottom" title={appointmentData.text}>
        <div className="training-tag">
          <BookOutlined />
        </div>
      </Tooltip>
    );
  };

  // Texto da máscara do card: só o Nome do Cliente.
  // A escolha está em Utils.readAgendaCardClientName, que não usa o Nome Fantasia.
  const clientName = Utils.readAgendaCardClientName(appointmentData);

  const renderClientDescription = () => {
    if (type) {
      return null;
    }

    if (!clientName) {
      return null;
    }

    return <p className="card-description">{clientName}</p>;
  };

  const renderTransportTag = () => {
    if (type) {
      return null;
    }

    const transportIcon = Utils.resolveAgendaTransportIcon(appointmentData);

    if (!transportIcon.visible) {
      return null;
    }

    return (
      <Tooltip placement="bottom" title={renderAgendaTransportTooltipTitle(transportIcon)}>
        <div className="transport-tag">
          <FaBus style={{ color: transportIcon.color }} />
        </div>
      </Tooltip>
    );
  };

  return (
    <div
      ref={appointmentRootRef}
      id="appointment-task"
      className={className ?? ''}
      style={{
        color: stage === 1 ? 'white' : '',
      }}
    >
      <div className="top-bar" />
      <h5 className="class-id" style={{ color: '#121212' }}>
        {Utils.formatAgendaGridCardTitle(appointmentData)}
      </h5>
      <div className="body" style={{ color: '#121212' }}>
        {renderClientDescription()}

        {!type && appointmentData.quoteOrDealUpdated && (
          <Tooltip placement="top" title="Informações Atualizadas">
            <div className="updated-tag">
              <InfoOutlined />
            </div>
          </Tooltip>
        )}

        <div className="tags-1">
          {renderTrainingTag()}

          {!type && (
            <Tooltip placement="bottom" title={quoteResponsibleTagTitle}>
              <div className="quote-responsible-tag">
                <FaRegHandshake style={{ color: QUOTE_RESPONSIBLE_ICON_COLOR }} />
              </div>
            </Tooltip>
          )}

          <Tooltip placement="bottom" title="Alunos">
            <div className="students-tag">
              <TeamOutlined />
              <p className="card-text">{appointmentData.qtyStudentsPerClass}</p>
            </div>
          </Tooltip>

          {!type && (
            <Tooltip placement="bottom" title={instructorTagTitle}>
              <div className="instructor-tag">
                <UserOutlined style={{ color: mainInstructorStatusColor() }} />
              </div>
            </Tooltip>
          )}

          {renderTransportTag()}
        </div>

        <div className="tags-2">
          <Tooltip placement="bottom" title="Estágio">
            <div className="tag stage-tag" style={{ backgroundColor, color: textColor }}>
              <p className="card-text">{stageObject?.name}</p>
            </div>
          </Tooltip>

          {(appointmentData.sharedTraining || appointmentData.acceptSharedTraining) && (
            <Tooltip placement="bottom" title="Treinamento Compartilhado">
              <div className="tag shared-training-tag">
                <ShareAltOutlined />
              </div>
            </Tooltip>
          )}
        </div>
        <div className={className ?? ''}>
          {isTooltip && (
            <div className="wrapper-action">
              {appointmentData.stage.id !== 5 && (
                <Tooltip title="Deletar" placement="right">
                  <DeleteOutlined className="delete-icon" onClick={handleDelete} />
                </Tooltip>
              )}

              {appointmentData.stage.id !== 5 && (
                <Tooltip title="Reagendar" placement="right">
                  <CalendarOutlined
                    className="reschedule-icon"
                    onClick={() => {
                      removeAppointmentByIndex(appointmentData.index);
                      setTaskRescheduling([
                        {
                          id: appointmentData.dealId,
                          name: appointmentData.quoteTitle,
                          trainingsIndicator: '',
                          tasks: [appointmentData],
                        },
                      ]);
                    }}
                  />
                </Tooltip>
              )}

              <Tooltip title="Copiar Detalhes do Treinamento" placement="right">
                <CopyOutlined
                  className="copy-icon"
                  onClick={(e) => Utils.onCopyDetails(appointmentData, e)}
                />
              </Tooltip>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
